import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Settings, Heart, Languages, BookOpen, RotateCcw, Play, Pause } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import VerseItem from '../components/VerseItem';
import AudioPlayer from '../components/AudioPlayer';
import TafsirSheet from '../components/TafsirSheet';
import MemorizationMode from '../components/MemorizationMode';
import { quranApi } from '../lib/api';
import useQuranStore from '../store/quranStore';

const TRANSLATIONS = [
  { value: 'english', label: 'English' },
  { value: 'urdu', label: 'اردو (Urdu)' },
  { value: 'bengali', label: 'বাংলা (Bengali)' },
  { value: 'turkish', label: 'Türkçe (Turkish)' },
  { value: 'uzbek', label: "O'zbek (Uzbek)" },
];

// Surah names for navigation
const SURAH_NAMES = [
  "Al-Faatiha", "Al-Baqara", "Aal-i-Imraan", "An-Nisaa", "Al-Maaida", "Al-An'aam", "Al-A'raaf", "Al-Anfaal", "At-Tawba", "Yunus",
  "Hud", "Yusuf", "Ar-Ra'd", "Ibrahim", "Al-Hijr", "An-Nahl", "Al-Israa", "Al-Kahf", "Maryam", "Ta-Ha",
  "Al-Anbiyaa", "Al-Hajj", "Al-Mu'minoon", "An-Noor", "Al-Furqaan", "Ash-Shu'araa", "An-Naml", "Al-Qasas", "Al-Ankaboot", "Ar-Room",
  "Luqman", "As-Sajda", "Al-Ahzaab", "Saba", "Faatir", "Ya-Seen", "As-Saaffaat", "Saad", "Az-Zumar", "Ghafir",
  "Fussilat", "Ash-Shura", "Az-Zukhruf", "Ad-Dukhaan", "Al-Jaathiya", "Al-Ahqaaf", "Muhammad", "Al-Fath", "Al-Hujuraat", "Qaaf",
  "Adh-Dhaariyat", "At-Toor", "An-Najm", "Al-Qamar", "Ar-Rahmaan", "Al-Waqi'a", "Al-Hadid", "Al-Mujaadila", "Al-Hashr", "Al-Mumtahana",
  "As-Saff", "Al-Jumu'a", "Al-Munaafiqoon", "At-Taghaabun", "At-Talaaq", "At-Tahrim", "Al-Mulk", "Al-Qalam", "Al-Haaqqa", "Al-Ma'aarij",
  "Nooh", "Al-Jinn", "Al-Muzzammil", "Al-Muddaththir", "Al-Qiyaama", "Al-Insaan", "Al-Mursalaat", "An-Naba", "An-Naazi'aat", "Abasa",
  "At-Takwir", "Al-Infitaar", "Al-Mutaffifin", "Al-Inshiqaaq", "Al-Burooj", "At-Taariq", "Al-A'laa", "Al-Ghaashiya", "Al-Fajr", "Al-Balad",
  "Ash-Shams", "Al-Lail", "Ad-Dhuha", "Ash-Sharh", "At-Tin", "Al-Alaq", "Al-Qadr", "Al-Bayyina", "Az-Zalzala", "Al-Aadiyaat",
  "Al-Qaari'a", "At-Takaathur", "Al-Asr", "Al-Humaza", "Al-Fil", "Quraish", "Al-Maa'oon", "Al-Kawthar", "Al-Kaafiroon", "An-Nasr",
  "Al-Masad", "Al-Ikhlaas", "Al-Falaq", "An-Naas"
];

export default function SurahPage() {
  const { surahNo } = useParams();
  const [searchParams] = useSearchParams();
  const surahNumber = parseInt(surahNo);

  const [surah, setSurah] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Audio state
  const [currentAyah, setCurrentAyah] = useState(1);
  const [audioData, setAudioData] = useState(null);
  
  // Tafsir state
  const [tafsirOpen, setTafsirOpen] = useState(false);
  const [tafsirAyah, setTafsirAyah] = useState(null);
  
  // Bismillah audio state
  const [bismillahPlaying, setBismillahPlaying] = useState(false);
  const [bismillahAudioData, setBismillahAudioData] = useState(null);

  // Memorization mode
  const [memorizationMode, setMemorizationMode] = useState(false);
  const [memorizationAyah, setMemorizationAyah] = useState(null);

  // Store
  const { 
    setLastReading, 
    fontSize, 
    setFontSize,
    showDiacritics,
    setShowDiacritics,
    translationLang,
    setTranslationLang,
    isFavorite,
    addFavorite,
    removeFavorite,
  } = useQuranStore();

  // Load surah data and reset audio state when surah changes
  useEffect(() => {
    const loadSurah = async () => {
      try {
        setLoading(true);
        setError(null);
        // Reset audio state when navigating to a new surah
        setAudioData(null);
        setCurrentAyah(1);
        const data = await quranApi.getSurah(surahNumber);
        setSurah(data);
      } catch (err) {
        console.error('Error loading surah:', err);
        setError('Failed to load surah. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadSurah();
  }, [surahNumber]);

  // Set initial ayah from URL
  useEffect(() => {
    const ayahParam = searchParams.get('ayah');
    if (ayahParam) {
      setCurrentAyah(parseInt(ayahParam));
    }
  }, [searchParams]);

  // Update last reading position
  useEffect(() => {
    if (surah && currentAyah) {
      setLastReading(surahNumber, currentAyah, surah.surahName);
    }
  }, [surah, currentAyah, surahNumber, setLastReading]);

  // Load audio for current ayah
  const loadAudioForAyah = useCallback(async (ayahNo) => {
    try {
      const audio = await quranApi.getVerseAudio(surahNumber, ayahNo);
      setAudioData(audio);
      setCurrentAyah(ayahNo);
    } catch (err) {
      console.error('Error loading audio:', err);
    }
  }, [surahNumber]);

  // Preload audio for next verse (for smooth playback)
  const preloadAudioForAyah = useCallback(async (ayahNo) => {
    try {
      const audio = await quranApi.getVerseAudio(surahNumber, ayahNo);
      return audio;
    } catch (err) {
      console.error('Error preloading audio:', err);
      return null;
    }
  }, [surahNumber]);

  // Play Bismillah audio (Surah 1, Ayah 1)
  const handlePlayBismillah = async () => {
    try {
      if (bismillahPlaying && audioData && bismillahAudioData) {
        // Stop playing
        setBismillahPlaying(false);
        setAudioData(null);
        return;
      }
      const audio = await quranApi.getVerseAudio(1, 1);
      setBismillahAudioData(audio);
      setBismillahPlaying(true);
      setAudioData(audio);
      setCurrentAyah(0); // Use 0 to indicate bismillah, not a real verse
    } catch (err) {
      console.error('Error loading bismillah audio:', err);
    }
  };

  // Play audio for a verse
  const handlePlayAudio = (ayahNo) => {
    setBismillahPlaying(false);
    loadAudioForAyah(ayahNo);
  };

  // Show tafsir
  const handleShowTafsir = (ayahNo) => {
    setTafsirAyah(ayahNo);
    setTafsirOpen(true);
  };
  
  // Start memorization for a verse
  const handleStartMemorization = (ayahNo) => {
    setMemorizationAyah(ayahNo);
    setMemorizationMode(true);
  };

  // Handle ayah change from audio player (auto-advance)
  const handleAyahChange = (newAyah) => {
    setBismillahPlaying(false);
    loadAudioForAyah(newAyah);
    // Scroll to the verse
    setTimeout(() => {
      const verseElement = document.querySelector(`[data-testid="verse-${surahNumber}-${newAyah}"]`);
      if (verseElement) {
        verseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Toggle favorite
  const isFav = isFavorite(surahNumber);
  const handleFavorite = () => {
    if (isFav) {
      removeFavorite(surahNumber);
    } else if (surah) {
      addFavorite({
        surahNo: surahNumber,
        surahName: surah.surahName,
        surahNameArabic: surah.surahNameArabic,
      });
    }
  };

  // Build verses array from surah data
  const getVerses = () => {
    if (!surah) return [];
    
    const verses = [];
    const totalAyahs = surah.totalAyah;
    
    for (let i = 0; i < totalAyahs; i++) {
      verses.push({
        arabic1: surah.arabic1?.[i] || '',
        arabic2: surah.arabic2?.[i] || '',
        english: surah.english?.[i] || '',
        urdu: surah.urdu?.[i] || '',
        bengali: surah.bengali?.[i] || '',
        turkish: surah.turkish?.[i] || '',
        uzbek: surah.uzbek?.[i] || '',
      });
    }
    
    return verses;
  };

  const verses = getVerses();
  
  // Get previous and next surah names
  const prevSurahName = surahNumber > 1 ? SURAH_NAMES[surahNumber - 2] : null;
  const nextSurahName = surahNumber < 114 ? SURAH_NAMES[surahNumber] : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-24">
        <p className="text-destructive mb-4">{error}</p>
        <button onClick={loadSurah} className="text-primary underline">
          Try again
        </button>
      </div>
    );
  }

  if (!surah) return null;

  return (
    <div className="min-h-screen pb-32" data-testid="surah-page">
      {/* Surah header */}
      <div className="sticky z-30 glass border-b border-border/40" style={{ top: 'calc(4rem + env(safe-area-inset-top, 0px))' }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            {/* Surah info */}
            <div className="text-center flex-1">
              <h1 className="font-serif text-xl font-semibold">{surah.surahName}</h1>
              <p className="font-arabic text-lg text-muted-foreground">
                {surah.surahNameArabic}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Translation selector */}
              <Select value={translationLang} onValueChange={setTranslationLang}>
                <SelectTrigger className="w-[100px] sm:w-[130px] h-9" data-testid="surah-translation-select">
                  <Languages className="w-4 h-4 mr-1 shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSLATIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Favorite */}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleFavorite}
                data-testid="surah-favorite-btn"
              >
                <Heart className={`w-5 h-5 ${isFav ? 'fill-red-500 text-red-500' : ''}`} />
              </Button>

              {/* Settings */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="reader-settings-btn">
                    <Settings className="w-5 h-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="end">
                  <div className="space-y-4">
                    <h4 className="font-medium">Reading Settings</h4>
                    
                    {/* Font size */}
                    <div>
                      <label className="text-sm text-muted-foreground mb-2 block">
                        Font Size
                      </label>
                      <Slider
                        value={[fontSize]}
                        min={0.8}
                        max={1.5}
                        step={0.1}
                        onValueChange={([v]) => setFontSize(v)}
                        data-testid="font-size-slider"
                      />
                    </div>

                    {/* Diacritics toggle */}
                    <div className="flex items-center justify-between">
                      <label className="text-sm">Show Diacritics (Tashkeel)</label>
                      <Switch
                        checked={showDiacritics}
                        onCheckedChange={setShowDiacritics}
                        data-testid="diacritics-toggle"
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {/* Surah content */}
      <div className="container mx-auto max-w-3xl px-4 py-8">
        {/* Surah title ornamental frame */}
        <div className="surah-title-frame">
          <p className="font-arabic text-3xl md:text-4xl text-foreground mb-3">
            {surah.surahNameArabic}
          </p>
          <h2 className="font-serif text-lg font-semibold text-foreground/80 mb-1">
            {surah.surahName}
          </h2>
          <p className="text-sm text-muted-foreground">
            {surah.surahNameTranslation} · {surah.revelationPlace} · {surah.totalAyah} Ayahs
          </p>
        </div>

        {/* Bismillah (except for Surah 9) */}
        {surahNumber !== 9 && (
          <div className="bismillah-frame mb-8">
            <div className="bismillah-corners relative">
              <div className="bismillah-flourish">
                <span className="text-primary/30 text-lg font-arabic">﴾</span>
              </div>
              <div className="bismillah mb-2">
                بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
              </div>
              <div className="bismillah-flourish mb-4">
                <span className="text-primary/30 text-lg font-arabic">﴿</span>
              </div>
              <div className="flex justify-center">
                <Button
                  variant={bismillahPlaying ? 'default' : 'outline'}
                  size="sm"
                  onClick={handlePlayBismillah}
                  className="gap-2"
                  data-testid="play-bismillah"
                >
                  {bismillahPlaying ? (
                    <>
                      <Pause className="w-3 h-3" />
                      Playing Bismillah
                    </>
                  ) : (
                    <>
                      <Play className="w-3 h-3" />
                      Play Bismillah
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Verses */}
        <div className="quran-page">
          {verses.map((verse, index) => (
            <VerseItem
              key={index}
              verse={verse}
              ayahNo={index + 1}
              surahNo={surahNumber}
              surahName={surah.surahName}
              onPlayAudio={handlePlayAudio}
              onShowTafsir={handleShowTafsir}
              onStartMemorization={handleStartMemorization}
              isCurrentlyPlaying={currentAyah === index + 1 && audioData !== null}
            />
          ))}
        </div>
        
        {/* Bottom Navigation - Previous/Next Surah */}
        <div className="mt-16 pt-8 border-t border-border/30">
          <div className="flex items-center justify-between gap-4">
            {/* Previous Surah */}
            {surahNumber > 1 ? (
              <Link to={`/surah/${surahNumber - 1}`} className="flex-1">
                <Button 
                  variant="outline" 
                  className="w-full h-auto py-4 flex flex-col items-start gap-1"
                  data-testid="bottom-prev-surah-btn"
                >
                  <span className="flex items-center text-xs text-muted-foreground">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous Surah
                  </span>
                  <span className="font-serif text-sm sm:text-base font-medium">
                    {surahNumber - 1}. {prevSurahName}
                  </span>
                </Button>
              </Link>
            ) : (
              <div className="flex-1" />
            )}

            {/* Next Surah */}
            {surahNumber < 114 ? (
              <Link to={`/surah/${surahNumber + 1}`} className="flex-1">
                <Button 
                  variant="outline" 
                  className="w-full h-auto py-4 flex flex-col items-end gap-1"
                  data-testid="bottom-next-surah-btn"
                >
                  <span className="flex items-center text-xs text-muted-foreground">
                    Next Surah
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                  <span className="font-serif text-sm sm:text-base font-medium">
                    {surahNumber + 1}. {nextSurahName}
                  </span>
                </Button>
              </Link>
            ) : (
              <div className="flex-1" />
            )}
          </div>
        </div>
      </div>

      {/* Audio Player */}
      {audioData && (
        <AudioPlayer
          audioData={audioData}
          surahNo={surahNumber}
          currentAyah={currentAyah}
          totalAyahs={surah.totalAyah}
          onAyahChange={handleAyahChange}
          onPreloadNext={preloadAudioForAyah}
          surahName={surah.surahName}
        />
      )}

      {/* Tafsir Sheet */}
      <TafsirSheet
        isOpen={tafsirOpen}
        onClose={() => setTafsirOpen(false)}
        surahNo={surahNumber}
        ayahNo={tafsirAyah}
        surahName={surah.surahName}
      />
      
      {/* Memorization Mode */}
      {memorizationMode && memorizationAyah && (
        <MemorizationMode
          isOpen={memorizationMode}
          onClose={() => setMemorizationMode(false)}
          surahNo={surahNumber}
          ayahNo={memorizationAyah}
          surahName={surah.surahName}
          verse={verses[memorizationAyah - 1]}
        />
      )}
    </div>
  );
}
